import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, AlertTriangle, Mail, ExternalLink } from 'lucide-react';
import { submissionsApi, newslettersApi, formConfigsApi, emailTemplatesApi, extractLookupId } from '../services/creatorApi';
// ── Build marker ──
const BUILD_ID = 'v14-' + '20260320T1200';
// ── i18n ──────────────────────────────────────────────────────────────────────
interface Translations {
  confirmingTitle: string;
  confirmingSubtitle: string;
  confirmedTitle: string;
  confirmedSubtitle: string;
  redirectingIn: (n: number) => string;
  clickHere: string;
  successSubtitle: string;
  allSet: string;
  invalidTitle: string;
  invalidSubtitle: string;
  errorTitle: string;
  errorSubtitle: string;
  tryAgain: string;
  poweredBy: string;
}
const LANGUAGES: Record<string, {
  label: string;
  flag: string;
  region: string;
  t: Translations;
}> = {
  en: {
    label: 'English',
    flag: '🇺🇸',
    region: 'Global',
    t: {
      confirmingTitle: 'Confirming your subscription...',
      confirmingSubtitle: 'Please wait while we verify your email address.',
      confirmedTitle: 'Subscription confirmed!',
      confirmedSubtitle: "Your email has been verified. You're now subscribed.",
      redirectingIn: n => `Redirecting in ${n} second${n !== 1 ? 's' : ''}...`,
      clickHere: 'Click here if not redirected automatically',
      successSubtitle: "Your email has been verified and your subscription is now active. You'll start receiving updates soon.",
      allSet: "You're all set!",
      invalidTitle: 'Invalid confirmation link',
      invalidSubtitle: 'This confirmation link is invalid or has already been used. Please try signing up again.',
      errorTitle: 'Something went wrong',
      errorSubtitle: "We couldn't process your confirmation. Please try again.",
      tryAgain: 'Try Again',
      poweredBy: 'Powered by The Chosen Campaign Manager'
    }
  },
  'pt-BR': {
    label: 'Português (Brasil)',
    flag: '🇧🇷',
    region: 'Brasil',
    t: {
      confirmingTitle: 'Confirmando sua inscrição...',
      confirmingSubtitle: 'Por favor, aguarde enquanto verificamos seu e-mail.',
      confirmedTitle: 'Inscrição confirmada!',
      confirmedSubtitle: 'Seu e-mail foi verificado. Agora você está inscrito.',
      redirectingIn: n => `Redirecionando em ${n} segundo${n !== 1 ? 's' : ''}...`,
      clickHere: 'Clique aqui se não for redirecionado automaticamente',
      successSubtitle: 'Seu e-mail foi verificado e sua inscrição está ativa. Você começará a receber atualizações em breve.',
      allSet: 'Tudo pronto!',
      invalidTitle: 'Link de confirmação inválido',
      invalidSubtitle: 'Este link de confirmação é inválido ou já foi utilizado. Por favor, tente se inscrever novamente.',
      errorTitle: 'Algo deu errado',
      errorSubtitle: 'Não foi possível processar sua confirmação. Por favor, tente novamente.',
      tryAgain: 'Tentar Novamente',
      poweredBy: 'Desenvolvido por The Chosen Campaign Manager'
    }
  },
  es: {
    label: 'Español',
    flag: '🇪🇸',
    region: 'LATAM',
    t: {
      confirmingTitle: 'Confirmando tu suscripción...',
      confirmingSubtitle: 'Por favor espera mientras verificamos tu correo electrónico.',
      confirmedTitle: '¡Suscripción confirmada!',
      confirmedSubtitle: 'Tu correo ha sido verificado. Ya estás suscrito.',
      redirectingIn: n => `Redirigiendo en ${n} segundo${n !== 1 ? 's' : ''}...`,
      clickHere: 'Haz clic aquí si no eres redirigido automáticamente',
      successSubtitle: 'Tu correo ha sido verificado y tu suscripción está activa. Comenzarás a recibir actualizaciones pronto.',
      allSet: '¡Todo listo!',
      invalidTitle: 'Enlace de confirmación inválido',
      invalidSubtitle: 'Este enlace de confirmación es inválido o ya ha sido utilizado. Por favor, intenta registrarte de nuevo.',
      errorTitle: 'Algo salió mal',
      errorSubtitle: 'No pudimos procesar tu confirmación. Por favor, inténtalo de nuevo.',
      tryAgain: 'Intentar de Nuevo',
      poweredBy: 'Desarrollado por The Chosen Campaign Manager'
    }
  },
  fr: {
    label: 'Français',
    flag: '🇫🇷',
    region: 'EMEA',
    t: {
      confirmingTitle: 'Confirmation de votre inscription...',
      confirmingSubtitle: 'Veuillez patienter pendant que nous vérifions votre adresse e-mail.',
      confirmedTitle: 'Inscription confirmée !',
      confirmedSubtitle: 'Votre e-mail a été vérifié. Vous êtes maintenant inscrit.',
      redirectingIn: n => `Redirection dans ${n} seconde${n !== 1 ? 's' : ''}...`,
      clickHere: "Cliquez ici si vous n'êtes pas redirigé automatiquement",
      successSubtitle: 'Votre e-mail a été vérifié et votre inscription est active. Vous commencerez à recevoir des mises à jour bientôt.',
      allSet: 'Vous êtes prêt !',
      invalidTitle: 'Lien de confirmation invalide',
      invalidSubtitle: 'Ce lien de confirmation est invalide ou a déjà été utilisé. Veuillez vous inscrire à nouveau.',
      errorTitle: 'Une erreur est survenue',
      errorSubtitle: "Nous n'avons pas pu traiter votre confirmation. Veuillez réessayer.",
      tryAgain: 'Réessayer',
      poweredBy: 'Propulsé par The Chosen Campaign Manager'
    }
  },
  de: {
    label: 'Deutsch',
    flag: '🇩🇪',
    region: 'EMEA',
    t: {
      confirmingTitle: 'Bestätigung Ihres Abonnements...',
      confirmingSubtitle: 'Bitte warten Sie, während wir Ihre E-Mail-Adresse überprüfen.',
      confirmedTitle: 'Abonnement bestätigt!',
      confirmedSubtitle: 'Ihre E-Mail wurde verifiziert. Sie sind jetzt abonniert.',
      redirectingIn: n => `Weiterleitung in ${n} Sekunde${n !== 1 ? 'n' : ''}...`,
      clickHere: 'Klicken Sie hier, wenn Sie nicht automatisch weitergeleitet werden',
      successSubtitle: 'Ihre E-Mail wurde verifiziert und Ihr Abonnement ist jetzt aktiv. Sie werden bald Updates erhalten.',
      allSet: 'Alles bereit!',
      invalidTitle: 'Ungültiger Bestätigungslink',
      invalidSubtitle: 'Dieser Bestätigungslink ist ungültig oder wurde bereits verwendet. Bitte versuchen Sie, sich erneut anzumelden.',
      errorTitle: 'Etwas ist schiefgelaufen',
      errorSubtitle: 'Wir konnten Ihre Bestätigung nicht verarbeiten. Bitte versuchen Sie es erneut.',
      tryAgain: 'Erneut versuchen',
      poweredBy: 'Bereitgestellt von The Chosen Campaign Manager'
    }
  },
  it: {
    label: 'Italiano',
    flag: '🇮🇹',
    region: 'EMEA',
    t: {
      confirmingTitle: "Conferma dell'iscrizione in corso...",
      confirmingSubtitle: 'Attendere mentre verifichiamo il tuo indirizzo email.',
      confirmedTitle: 'Iscrizione confermata!',
      confirmedSubtitle: 'La tua email è stata verificata. Ora sei iscritto.',
      redirectingIn: n => `Reindirizzamento tra ${n} second${n !== 1 ? 'i' : 'o'}...`,
      clickHere: 'Clicca qui se non vieni reindirizzato automaticamente',
      successSubtitle: 'La tua email è stata verificata e la tua iscrizione è ora attiva. Inizierai a ricevere aggiornamenti presto.',
      allSet: 'Tutto pronto!',
      invalidTitle: 'Link di conferma non valido',
      invalidSubtitle: 'Questo link di conferma non è valido o è già stato utilizzato. Riprova a registrarti.',
      errorTitle: 'Qualcosa è andato storto',
      errorSubtitle: 'Non siamo riusciti a elaborare la tua conferma. Riprova.',
      tryAgain: 'Riprova',
      poweredBy: 'Fornito da The Chosen Campaign Manager'
    }
  },
  ar: {
    label: 'العربية',
    flag: '🇸🇦',
    region: 'EMEA',
    t: {
      confirmingTitle: 'جارٍ تأكيد اشتراكك...',
      confirmingSubtitle: 'يرجى الانتظار بينما نتحقق من عنوان بريدك الإلكتروني.',
      confirmedTitle: 'تم تأكيد الاشتراك!',
      confirmedSubtitle: 'تم التحقق من بريدك الإلكتروني. أنت الآن مشترك.',
      redirectingIn: n => `إعادة التوجيه خلال ${n} ثانية...`,
      clickHere: 'انقر هنا إذا لم تتم إعادة التوجيه تلقائيًا',
      successSubtitle: 'تم التحقق من بريدك الإلكتروني واشتراكك نشط الآن. ستبدأ في تلقي التحديثات قريبًا.',
      allSet: 'كل شيء جاهز!',
      invalidTitle: 'رابط التأكيد غير صالح',
      invalidSubtitle: 'رابط التأكيد هذا غير صالح أو تم استخدامه بالفعل. يرجى محاولة التسجيل مرة أخرى.',
      errorTitle: 'حدث خطأ ما',
      errorSubtitle: 'لم نتمكن من معالجة تأكيدك. يرجى المحاولة مرة أخرى.',
      tryAgain: 'حاول مرة أخرى',
      poweredBy: 'مدعوم من The Chosen Campaign Manager'
    }
  },
  tr: {
    label: 'Türkçe',
    flag: '🇹🇷',
    region: 'EMEA',
    t: {
      confirmingTitle: 'Aboneliğiniz onaylanıyor...',
      confirmingSubtitle: 'E-posta adresinizi doğrularken lütfen bekleyin.',
      confirmedTitle: 'Abonelik onaylandı!',
      confirmedSubtitle: 'E-postanız doğrulandı. Artık abone oldunuz.',
      redirectingIn: n => `${n} saniye içinde yönlendiriliyorsunuz...`,
      clickHere: 'Otomatik yönlendirilmediyseniz buraya tıklayın',
      successSubtitle: 'E-postanız doğrulandı ve aboneliğiniz artık aktif. Yakında güncellemeler almaya başlayacaksınız.',
      allSet: 'Hazırsınız!',
      invalidTitle: 'Geçersiz onay bağlantısı',
      invalidSubtitle: 'Bu onay bağlantısı geçersiz veya zaten kullanılmış. Lütfen tekrar kaydolmayı deneyin.',
      errorTitle: 'Bir şeyler yanlış gitti',
      errorSubtitle: 'Onayınızı işleyemedik. Lütfen tekrar deneyin.',
      tryAgain: 'Tekrar Dene',
      poweredBy: 'The Chosen Campaign Manager tarafından desteklenmektedir'
    }
  },
  nl: {
    label: 'Nederlands',
    flag: '🇳🇱',
    region: 'EMEA',
    t: {
      confirmingTitle: 'Uw abonnement bevestigen...',
      confirmingSubtitle: 'Even geduld terwijl we uw e-mailadres verifiëren.',
      confirmedTitle: 'Abonnement bevestigd!',
      confirmedSubtitle: 'Uw e-mail is geverifieerd. U bent nu geabonneerd.',
      redirectingIn: n => `Doorverwijzing over ${n} seconde${n !== 1 ? 'n' : ''}...`,
      clickHere: 'Klik hier als u niet automatisch wordt doorverwezen',
      successSubtitle: 'Uw e-mail is geverifieerd en uw abonnement is nu actief. U ontvangt binnenkort updates.',
      allSet: 'U bent klaar!',
      invalidTitle: 'Ongeldige bevestigingslink',
      invalidSubtitle: 'Deze bevestigingslink is ongeldig of is al gebruikt. Probeer opnieuw te registreren.',
      errorTitle: 'Er is iets misgegaan',
      errorSubtitle: 'We konden uw bevestiging niet verwerken. Probeer het opnieuw.',
      tryAgain: 'Opnieuw proberen',
      poweredBy: 'Mogelijk gemaakt door The Chosen Campaign Manager'
    }
  },
  pl: {
    label: 'Polski',
    flag: '🇵🇱',
    region: 'EMEA',
    t: {
      confirmingTitle: 'Potwierdzanie subskrypcji...',
      confirmingSubtitle: 'Proszę czekać, weryfikujemy Twój adres e-mail.',
      confirmedTitle: 'Subskrypcja potwierdzona!',
      confirmedSubtitle: 'Twój e-mail został zweryfikowany. Jesteś teraz subskrybentem.',
      redirectingIn: n => `Przekierowanie za ${n} sekund${n === 1 ? 'ę' : n < 5 ? 'y' : ''}...`,
      clickHere: 'Kliknij tutaj, jeśli nie zostaniesz przekierowany automatycznie',
      successSubtitle: 'Twój e-mail został zweryfikowany, a subskrypcja jest aktywna. Wkrótce zaczniesz otrzymywać aktualizacje.',
      allSet: 'Wszystko gotowe!',
      invalidTitle: 'Nieprawidłowy link potwierdzający',
      invalidSubtitle: 'Ten link potwierdzający jest nieprawidłowy lub został już użyty. Spróbuj zarejestrować się ponownie.',
      errorTitle: 'Coś poszło nie tak',
      errorSubtitle: 'Nie udało się przetworzyć potwierdzenia. Spróbuj ponownie.',
      tryAgain: 'Spróbuj ponownie',
      poweredBy: 'Obsługiwane przez The Chosen Campaign Manager'
    }
  },
  ru: {
    label: 'Русский',
    flag: '🇷🇺',
    region: 'EMEA',
    t: {
      confirmingTitle: 'Подтверждение подписки...',
      confirmingSubtitle: 'Пожалуйста, подождите, пока мы проверяем ваш адрес электронной почты.',
      confirmedTitle: 'Подписка подтверждена!',
      confirmedSubtitle: 'Ваш email подтверждён. Вы подписаны.',
      redirectingIn: n => `Перенаправление через ${n} секунд${n === 1 ? 'у' : n < 5 ? 'ы' : ''}...`,
      clickHere: 'Нажмите здесь, если перенаправление не произошло автоматически',
      successSubtitle: 'Ваш email подтверждён, и подписка активна. Скоро вы начнёте получать обновления.',
      allSet: 'Всё готово!',
      invalidTitle: 'Недействительная ссылка подтверждения',
      invalidSubtitle: 'Эта ссылка подтверждения недействительна или уже была использована. Попробуйте зарегистрироваться снова.',
      errorTitle: 'Что-то пошло не так',
      errorSubtitle: 'Не удалось обработать подтверждение. Попробуйте ещё раз.',
      tryAgain: 'Попробовать снова',
      poweredBy: 'Работает на The Chosen Campaign Manager'
    }
  },
  zh: {
    label: '中文',
    flag: '🇨🇳',
    region: 'APAC',
    t: {
      confirmingTitle: '正在确认您的订阅...',
      confirmingSubtitle: '请稍候，我们正在验证您的电子邮件地址。',
      confirmedTitle: '订阅已确认！',
      confirmedSubtitle: '您的电子邮件已验证。您现在已订阅。',
      redirectingIn: n => `${n}秒后重定向...`,
      clickHere: '如果未自动重定向，请点击此处',
      successSubtitle: '您的电子邮件已验证，订阅现已生效。您将很快开始收到更新。',
      allSet: '一切就绪！',
      invalidTitle: '确认链接无效',
      invalidSubtitle: '此确认链接无效或已被使用。请重新注册。',
      errorTitle: '出了点问题',
      errorSubtitle: '我们无法处理您的确认。请重试。',
      tryAgain: '重试',
      poweredBy: '由 The Chosen Campaign Manager 提供支持'
    }
  },
  ja: {
    label: '日本語',
    flag: '🇯🇵',
    region: 'Asia',
    t: {
      confirmingTitle: '登録を確認中...',
      confirmingSubtitle: 'メールアドレスを確認しています。しばらくお待ちください。',
      confirmedTitle: '登録が確認されました！',
      confirmedSubtitle: 'メールアドレスが確認されました。登録が完了しました。',
      redirectingIn: n => `${n}秒後にリダイレクトします...`,
      clickHere: '自動的にリダイレクトされない場合はこちらをクリック',
      successSubtitle: 'メールアドレスが確認され、登録が有効になりました。まもなく更新情報が届きます。',
      allSet: '準備完了！',
      invalidTitle: '無効な確認リンク',
      invalidSubtitle: 'この確認リンクは無効か、すでに使用されています。もう一度登録してください。',
      errorTitle: 'エラーが発生しました',
      errorSubtitle: '確認を処理できませんでした。もう一度お試しください。',
      tryAgain: '再試行',
      poweredBy: 'The Chosen Campaign Manager 提供'
    }
  },
  ko: {
    label: '한국어',
    flag: '🇰🇷',
    region: 'Asia',
    t: {
      confirmingTitle: '구독을 확인하는 중...',
      confirmingSubtitle: '이메일 주소를 확인하는 동안 잠시 기다려 주세요.',
      confirmedTitle: '구독이 확인되었습니다!',
      confirmedSubtitle: '이메일이 확인되었습니다. 이제 구독 중입니다.',
      redirectingIn: n => `${n}초 후 리디렉션됩니다...`,
      clickHere: '자동으로 리디렉션되지 않으면 여기를 클릭하세요',
      successSubtitle: '이메일이 확인되었으며 구독이 활성화되었습니다. 곧 업데이트를 받기 시작합니다.',
      allSet: '모든 준비가 완료되었습니다!',
      invalidTitle: '유효하지 않은 확인 링크',
      invalidSubtitle: '이 확인 링크는 유효하지 않거나 이미 사용되었습니다. 다시 가입해 주세요.',
      errorTitle: '문제가 발생했습니다',
      errorSubtitle: '확인을 처리할 수 없습니다. 다시 시도해 주세요.',
      tryAgain: '다시 시도',
      poweredBy: 'The Chosen Campaign Manager 제공'
    }
  },
  hi: {
    label: 'हिन्दी',
    flag: '🇮🇳',
    region: 'Asia',
    t: {
      confirmingTitle: 'आपकी सदस्यता की पुष्टि हो रही है...',
      confirmingSubtitle: 'कृपया प्रतीक्षा करें जब तक हम आपका ईमेल पता सत्यापित करते हैं।',
      confirmedTitle: 'सदस्यता की पुष्टि हो गई!',
      confirmedSubtitle: 'आपका ईमेल सत्यापित हो गया है। अब आप सदस्य हैं।',
      redirectingIn: n => `${n} सेकंड में रीडायरेक्ट हो रहा है...`,
      clickHere: 'यदि स्वचालित रूप से रीडायरेक्ट नहीं होता है तो यहाँ क्लिक करें',
      successSubtitle: 'आपका ईमेल सत्यापित हो गया है और आपकी सदस्यता अब सक्रिय है। आपको जल्द ही अपडेट मिलने शुरू हो जाएंगे।',
      allSet: 'सब तैयार है!',
      invalidTitle: 'अमान्य पुष्टि लिंक',
      invalidSubtitle: 'यह पुष्टि लिंक अमान्य है या पहले ही उपयोग किया जा चुका है। कृपया फिर से साइन अप करें।',
      errorTitle: 'कुछ गलत हो गया',
      errorSubtitle: 'हम आपकी पुष्टि संसाधित नहीं कर सके। कृपया पुनः प्रयास करें।',
      tryAgain: 'पुनः प्रयास करें',
      poweredBy: 'The Chosen Campaign Manager द्वारा संचालित'
    }
  },
  th: {
    label: 'ไทย',
    flag: '🇹🇭',
    region: 'APAC',
    t: {
      confirmingTitle: 'กำลังยืนยันการสมัครของคุณ...',
      confirmingSubtitle: 'กรุณารอสักครู่ขณะที่เราตรวจสอบที่อยู่อีเมลของคุณ',
      confirmedTitle: 'ยืนยันการสมัครแล้ว!',
      confirmedSubtitle: 'อีเมลของคุณได้รับการยืนยันแล้ว คุณเป็นสมาชิกแล้ว',
      redirectingIn: n => `กำลังเปลี่ยนเส้นทางใน ${n} วินาที...`,
      clickHere: 'คลิกที่นี่หากไม่ถูกเปลี่ยนเส้นทางโดยอัตโนมัติ',
      successSubtitle: 'อีเมลของคุณได้รับการยืนยันและการสมัครของคุณเปิดใช้งานแล้ว คุณจะเริ่มได้รับการอัปเดตเร็วๆ นี้',
      allSet: 'พร้อมแล้ว!',
      invalidTitle: 'ลิงก์ยืนยันไม่ถูกต้อง',
      invalidSubtitle: 'ลิงก์ยืนยันนี้ไม่ถูกต้องหรือถูกใช้ไปแล้ว กรุณาลองสมัครใหม่อีกครั้ง',
      errorTitle: 'เกิดข้อผิดพลาด',
      errorSubtitle: 'เราไม่สามารถดำเนินการยืนยันของคุณได้ กรุณาลองอีกครั้ง',
      tryAgain: 'ลองอีกครั้ง',
      poweredBy: 'ขับเคลื่อนโดย The Chosen Campaign Manager'
    }
  },
  vi: {
    label: 'Tiếng Việt',
    flag: '🇻🇳',
    region: 'APAC',
    t: {
      confirmingTitle: 'Đang xác nhận đăng ký của bạn...',
      confirmingSubtitle: 'Vui lòng đợi trong khi chúng tôi xác minh địa chỉ email của bạn.',
      confirmedTitle: 'Đã xác nhận đăng ký!',
      confirmedSubtitle: 'Email của bạn đã được xác minh. Bạn đã đăng ký thành công.',
      redirectingIn: n => `Chuyển hướng sau ${n} giây...`,
      clickHere: 'Nhấp vào đây nếu không được chuyển hướng tự động',
      successSubtitle: 'Email của bạn đã được xác minh và đăng ký đang hoạt động. Bạn sẽ sớm nhận được cập nhật.',
      allSet: 'Hoàn tất!',
      invalidTitle: 'Liên kết xác nhận không hợp lệ',
      invalidSubtitle: 'Liên kết xác nhận này không hợp lệ hoặc đã được sử dụng. Vui lòng thử đăng ký lại.',
      errorTitle: 'Đã xảy ra lỗi',
      errorSubtitle: 'Chúng tôi không thể xử lý xác nhận của bạn. Vui lòng thử lại.',
      tryAgain: 'Thử lại',
      poweredBy: 'Được cung cấp bởi The Chosen Campaign Manager'
    }
  },
  id: {
    label: 'Bahasa Indonesia',
    flag: '🇮🇩',
    region: 'APAC',
    t: {
      confirmingTitle: 'Mengonfirmasi langganan Anda...',
      confirmingSubtitle: 'Harap tunggu sementara kami memverifikasi alamat email Anda.',
      confirmedTitle: 'Langganan dikonfirmasi!',
      confirmedSubtitle: 'Email Anda telah diverifikasi. Anda sekarang berlangganan.',
      redirectingIn: n => `Mengalihkan dalam ${n} detik...`,
      clickHere: 'Klik di sini jika tidak dialihkan secara otomatis',
      successSubtitle: 'Email Anda telah diverifikasi dan langganan Anda sekarang aktif. Anda akan segera mulai menerima pembaruan.',
      allSet: 'Semua siap!',
      invalidTitle: 'Tautan konfirmasi tidak valid',
      invalidSubtitle: 'Tautan konfirmasi ini tidak valid atau sudah digunakan. Silakan coba mendaftar lagi.',
      errorTitle: 'Terjadi kesalahan',
      errorSubtitle: 'Kami tidak dapat memproses konfirmasi Anda. Silakan coba lagi.',
      tryAgain: 'Coba Lagi',
      poweredBy: 'Didukung oleh The Chosen Campaign Manager'
    }
  },
  ms: {
    label: 'Bahasa Melayu',
    flag: '🇲🇾',
    region: 'APAC',
    t: {
      confirmingTitle: 'Mengesahkan langganan anda...',
      confirmingSubtitle: 'Sila tunggu sementara kami mengesahkan alamat e-mel anda.',
      confirmedTitle: 'Langganan disahkan!',
      confirmedSubtitle: 'E-mel anda telah disahkan. Anda kini melanggan.',
      redirectingIn: n => `Mengalihkan dalam ${n} saat...`,
      clickHere: 'Klik di sini jika tidak dialihkan secara automatik',
      successSubtitle: 'E-mel anda telah disahkan dan langganan anda kini aktif. Anda akan mula menerima kemas kini tidak lama lagi.',
      allSet: 'Semua sedia!',
      invalidTitle: 'Pautan pengesahan tidak sah',
      invalidSubtitle: 'Pautan pengesahan ini tidak sah atau telah digunakan. Sila cuba mendaftar semula.',
      errorTitle: 'Sesuatu telah berlaku',
      errorSubtitle: 'Kami tidak dapat memproses pengesahan anda. Sila cuba lagi.',
      tryAgain: 'Cuba Lagi',
      poweredBy: 'Dikuasakan oleh The Chosen Campaign Manager'
    }
  },
  tl: {
    label: 'Tagalog',
    flag: '🇵🇭',
    region: 'APAC',
    t: {
      confirmingTitle: 'Kinukumpirma ang iyong subscription...',
      confirmingSubtitle: 'Mangyaring maghintay habang bine-verify namin ang iyong email address.',
      confirmedTitle: 'Nakumpirma na ang subscription!',
      confirmedSubtitle: 'Na-verify na ang iyong email. Naka-subscribe ka na.',
      redirectingIn: n => `Ire-redirect sa ${n} segundo...`,
      clickHere: 'I-click dito kung hindi ka na-redirect nang awtomatiko',
      successSubtitle: 'Na-verify na ang iyong email at aktibo na ang iyong subscription. Magsisimula ka nang makatanggap ng mga update.',
      allSet: 'Handa ka na!',
      invalidTitle: 'Hindi valid ang confirmation link',
      invalidSubtitle: 'Ang confirmation link na ito ay hindi valid o nagamit na. Mangyaring subukang mag-sign up muli.',
      errorTitle: 'May nangyaring mali',
      errorSubtitle: 'Hindi namin maproseso ang iyong kumpirmasyon. Mangyaring subukan muli.',
      tryAgain: 'Subukan Muli',
      poweredBy: 'Pinapagana ng The Chosen Campaign Manager'
    }
  }
};
/** Detect language from URL param only (set by admin in Form Configuration) */
function detectLanguage(searchParams: URLSearchParams): string {
  const paramLang = searchParams.get('lang');
  if (paramLang) {
    if (LANGUAGES[paramLang]) return paramLang;
    // Try matching prefix (e.g. "pt" → "pt-BR")
    const prefix = paramLang.toLowerCase();
    const match = Object.keys(LANGUAGES).find(k => k.toLowerCase().startsWith(prefix));
    if (match) return match;
  }
  return 'en';
}
// ── End i18n ──────────────────────────────────────────────────────────────────
type ConfirmState = 'loading' | 'success' | 'redirecting' | 'invalid' | 'error';
/** Returns true only if the URL is a real destination (not a Zoho Creator internal URL) */
function isValidRedirectUrl(url: string | undefined | null): boolean {
  if (!url || !url.trim()) return false;
  const u = url.trim();
  // Reject Zoho Creator app URLs — these are internal record permalinks, not redirect destinations
  if (/creatorapp\.zohopublic\.com/i.test(u)) return false;
  return /^https?:\/\//i.test(u);
}
/**
 * Look up the current Confirmation_Link from the email template via the newsletter chain.
 * Newsletter_ID → newsletter → Sign_Up_Form → form config → Email_Template → Confirmation_Link
 * This is always up-to-date regardless of what was baked into the embed code at generation time.
 */
async function lookupConfirmationLink(newsletterId: string): Promise<string | null> {
  try {
    console.log('[confirm] Looking up Confirmation_Link for Newsletter_ID:', newsletterId);
    const newsletters = await newslettersApi.list();
    const newsletter = newsletters.find(nl => nl.Newsletter_ID === newsletterId);
    if (!newsletter) {
      console.warn('[confirm] Newsletter not found for ID:', newsletterId);
      return null;
    }
    const formId = extractLookupId(newsletter.Sign_Up_Form);
    if (!formId) {
      console.warn('[confirm] No Sign_Up_Form on newsletter');
      return null;
    }
    const configs = await formConfigsApi.findByFormId(formId);
    if (!configs.length) {
      console.warn('[confirm] No form config found for formId:', formId);
      return null;
    }
    const templateId = extractLookupId(configs[0].Email_Template);
    if (!templateId) {
      console.warn('[confirm] No Email_Template on form config - checking all templates for this team/form');
      // Fallback: if no template is linked to the config, try to find ANY template
      // that might have a redirect URL configured for this form
      try {
        const allTemplates = await emailTemplatesApi.list();
        // Look for templates that match the team and have a redirect URL
        const teamTemplates = allTemplates.filter(t => t.Team === configs[0].Team && (t as any).Confirmation_Link);
        if (teamTemplates.length > 0) {
          // Use the first template with a redirect URL
          const template = teamTemplates[0];
          const rawLink = (template as any).Confirmation_Link;
          const url = rawLink ? typeof rawLink === 'string' ? rawLink : rawLink?.url || null : null;
          console.log('[confirm] Found Confirmation_Link from fallback template:', url);
          return url || null;
        }
      } catch (fallbackErr) {
        console.warn('[confirm] Fallback template lookup failed:', fallbackErr);
      }
      return null;
    }
    const template = await emailTemplatesApi.get(templateId);
    if (!template) {
      console.warn('[confirm] Email template not found for ID:', templateId);
      return null;
    }
    const rawLink = (template as any).Confirmation_Link;
    const url = rawLink ? typeof rawLink === 'string' ? rawLink : rawLink?.url || null : null;
    console.log('[confirm] Found Confirmation_Link:', url);
    return url || null;
  } catch (err) {
    console.error('[confirm] lookupConfirmationLink failed:', err);
    return null;
  }
}
export function ConfirmSubscription() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const recordId = searchParams.get('id') || undefined;
  const [state, setState] = useState<ConfirmState>('loading');
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);
  const [errorDetail, setErrorDetail] = useState<string>('');
  const [diagnostics, setDiagnostics] = useState<string>('');
  const lang = detectLanguage(searchParams);
  const t = LANGUAGES[lang]?.t || LANGUAGES.en.t;
  const [retryCount, setRetryCount] = useState(0);
  const runConfirmation = async () => {
    if (!token) {
      setState('invalid');
      return;
    }
    setState('loading');
    setErrorDetail('');
    setDiagnostics('');
    try {
      console.log(`[confirm-${BUILD_ID}] token:`, token, 'id:', recordId, 'retry:', retryCount);
      // Use the SAME callZohoAPI path that works for form submission and all other pages
      const result = await submissionsApi.confirm(token, recordId);
      if (!result.success) {
        setErrorDetail(result.errorDetail || result.error || 'Unknown error');
        if (result.diagnostics) setDiagnostics(result.diagnostics);
        setState(result.error === 'invalid_token' ? 'invalid' : 'error');
        return;
      }
      // Handle redirect
      // Priority: template Confirmation_Link (always up-to-date) > submission Redirect_URL (baked at submit time)
      const storedRedirect = result.submission?.Redirect_URL;
      const newsletterId = result.submission?.Newsletter_ID;
      console.log('[confirm] Redirect lookup:', {
        storedRedirect,
        newsletterId,
        hasSubmission: !!result.submission
      });
      let finalRedirect: string | null = null;
      // 1. PRIORITY: Use the submission's Redirect_URL first (set by conditional routing at submit time)
      console.log('[confirm] Checking stored redirect:', {
        storedRedirect,
        isValid: isValidRedirectUrl(storedRedirect)
      });
      if (isValidRedirectUrl(storedRedirect)) {
        finalRedirect = storedRedirect!.trim();
        console.log('[confirm] ✅ Using submission Redirect_URL (from conditional routing):', finalRedirect);
      }
      // 2. FALLBACK: If no submission redirect, try the email template's Confirmation_Link
      if (!finalRedirect && newsletterId) {
        try {
          const templateLink = await lookupConfirmationLink(newsletterId);
          console.log('[confirm] Template lookup result:', {
            templateLink,
            isValid: isValidRedirectUrl(templateLink)
          });
          if (isValidRedirectUrl(templateLink)) {
            finalRedirect = templateLink!.trim();
            console.log('[confirm] ✅ Using template Confirmation_Link (fallback):', finalRedirect);
          }
        } catch (err) {
          console.warn('[confirm] Template lookup failed:', err);
        }
      }
      if (finalRedirect) {
        console.log('[confirm] 🎯 Final redirect URL:', finalRedirect);
        setRedirectUrl(finalRedirect);
        setState('redirecting');
        return;
      }
      console.warn('[confirm] ⚠️ No valid redirect URL found, showing success page');
      setState('success');
    } catch (err: any) {
      setErrorDetail(err instanceof Error ? err.message : String(err));
      setDiagnostics(`build=${BUILD_ID} | uncaught: ${String(err).substring(0, 200)}`);
      setState('error');
    }
  };
  useEffect(() => {
    runConfirmation();
  }, [token, recordId]);
  // Countdown + redirect
  useEffect(() => {
    if (state !== 'redirecting' || !redirectUrl) return;
    if (countdown <= 0) {
      window.location.href = redirectUrl;
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [state, countdown, redirectUrl]);
  return <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4" data-id="element-3464" data-mp="2x6fxovjhd018">
      <div className="w-full max-w-md" data-id="element-3465" data-mp="2s6tiwho07bqb">
        {/* Loading */}
        {state === 'loading' && <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-10 text-center" data-id="element-3466" data-mp="222i56ty99nxg">
            <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-5" data-id="element-3467" data-mp="1x3ozvot4h38d">
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-3468" data-mp="2b3q155daucod" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-3469" data-mp="264d2zogz9j6h">
              {t.confirmingTitle}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-3470" data-mp="1idr7sxs2ntdw">{t.confirmingSubtitle}</p>
          </div>}

        {/* Redirecting with countdown */}
        {state === 'redirecting' && redirectUrl && <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-10 text-center" data-id="element-3471" data-mp="1s4lsvytl0mxr">
            <div className="mx-auto w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-5" data-id="element-3472" data-mp="1nddhwyclstih">
              <CheckCircle2 className="h-8 w-8 text-green-500" data-id="element-3473" data-mp="zmzqna1ueyoy" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-3474" data-mp="upq35zy0kx3r">
              {t.confirmedTitle}
            </h1>
            <p className="text-sm text-gray-500 mb-6" data-id="element-3475" data-mp="36bi5kcfugrnk">{t.confirmedSubtitle}</p>
            <div className="flex flex-col items-center gap-3 mb-6" data-id="element-3476" data-mp="3b39z8vuardhx">
              <div className="relative w-16 h-16" data-id="element-3477" data-mp="2xbloavvsav2j">
                <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64" data-id="element-3478" data-mp="2xcwpqp1rbqqh">
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#e5e7eb" strokeWidth="4" data-id="element-3479" data-mp="329wy56m2x5kr" />
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#22c55e" strokeWidth="4" strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 28}`} strokeDashoffset={`${2 * Math.PI * 28 * (1 - (3 - countdown) / 3)}`} style={{
                transition: 'stroke-dashoffset 1s linear'
              }} data-id="element-3480" data-mp="3u1cjyyftao9x" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-gray-800" data-id="element-3481" data-mp="1li2v8oq36b8p">
                  {countdown}
                </span>
              </div>
              <p className="text-sm text-gray-500" data-id="element-3482" data-mp="1bt1gt2z6w1wu">
                {t.redirectingIn(countdown)}
              </p>
            </div>
            <a href={redirectUrl} className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium underline underline-offset-2" data-id="element-3483" data-mp="16u82x9fzq8w5">
              {t.clickHere}
              <ExternalLink className="h-3.5 w-3.5" data-id="element-3484" data-mp="xuusadg4re9u" />
            </a>
          </div>}

        {/* Success (no redirect URL) */}
        {state === 'success' && <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-10 text-center" data-id="element-3485" data-mp="2s69h8s1bh9oj">
            <div className="mx-auto w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-5" data-id="element-3486" data-mp="2n87zcafmngen">
              <CheckCircle2 className="h-8 w-8 text-green-500" data-id="element-3487" data-mp="2uqxr6o2easq7" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-3488" data-mp="2zozifw9z5ycr">
              {t.confirmedTitle}
            </h1>
            <p className="text-sm text-gray-500 mb-6" data-id="element-3489" data-mp="34hsm0gtpj0n9">{t.successSubtitle}</p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium border border-green-200" data-id="element-3490" data-mp="39eslg8w1x55e">
              <Mail className="h-4 w-4" data-id="element-3491" data-mp="2gr6h2yf01whh" />
              {t.allSet}
            </div>
          </div>}

        {/* Invalid token */}
        {state === 'invalid' && <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-10 text-center" data-id="element-3492" data-mp="2lsm3ouwtsb1z">
            <div className="mx-auto w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mb-5" data-id="element-3493" data-mp="2pq0c4kcomesx">
              <AlertTriangle className="h-8 w-8 text-amber-500" data-id="element-3494" data-mp="51vpvreqg1vg" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-3495" data-mp="53yogztyb3bs">
              {t.invalidTitle}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-3496" data-mp="3ur18rzooypzw">{t.invalidSubtitle}</p>
            <div className="mt-5 flex flex-col items-center gap-2" data-id="element-3497" data-mp="3usuencrhd66u">
              <button onClick={() => {
            setRetryCount(c => c + 1);
            runConfirmation();
          }} className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors" data-id="element-3498" data-mp="djxmmv62drxs">
                {t.tryAgain}
              </button>
              {retryCount > 0 && <button onClick={async () => {
            setRetryCount(c => c + 1);
            setState('loading');
            setErrorDetail('');
            setDiagnostics('');
            await new Promise(r => setTimeout(r, 10000));
            runConfirmation();
          }} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors" data-id="element-3499" data-mp="w8uhb05dimgj">
                  Retry with 10s delay
                </button>}
            </div>
            {errorDetail && <p className="mt-4 text-xs text-red-400 font-mono break-all bg-red-50 rounded p-2" data-id="element-3500" data-mp="1fuec33es4gnx">
                {errorDetail}
              </p>}
            {diagnostics && <details className="mt-3 text-left" data-id="element-3501" data-mp="15xjeqnu8smb8">
                <summary className="text-xs text-gray-400 cursor-pointer" data-id="element-3502" data-mp="15yb0z81y97ou">
                  Debug info (tap to expand)
                </summary>
                <p className="mt-1 text-[10px] text-gray-400 font-mono break-all bg-gray-50 rounded p-2 max-h-48 overflow-auto" data-id="element-3503" data-mp="w6orph969ioq">
                  {diagnostics}
                </p>
              </details>}
            <p className="mt-2 text-[10px] text-gray-300 font-mono" data-id="element-3504" data-mp="n5grgtn3i7s0">
              {BUILD_ID} · retry:{retryCount}
            </p>
          </div>}

        {/* Error */}
        {state === 'error' && <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-10 text-center" data-id="element-3505" data-mp="2k4611crjshut">
            <div className="mx-auto w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-5" data-id="element-3506" data-mp="2g45mls4g86nr">
              <XCircle className="h-8 w-8 text-red-500" data-id="element-3507" data-mp="2b5u8h574f9zq" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-3508" data-mp="37tij724us099">
              {t.errorTitle}
            </h1>
            <p className="text-sm text-gray-500 mb-5" data-id="element-3509" data-mp="33tgeek3tmqpr">{t.errorSubtitle}</p>
            <button onClick={() => {
          setRetryCount(c => c + 1);
          runConfirmation();
        }} className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors" data-id="element-3510" data-mp="2yt2g3ts0uv4x">
              {t.tryAgain}
            </button>
            {errorDetail && <p className="mt-4 text-xs text-red-400 font-mono break-all bg-red-50 rounded p-2" data-id="element-3511" data-mp="18cgfso878oak">
                {errorDetail}
              </p>}
            {diagnostics && <details className="mt-3 text-left" data-id="element-3512" data-mp="1i9bbkzd38al5">
                <summary className="text-xs text-gray-400 cursor-pointer" data-id="element-3513" data-mp="1i7rux7nirxiu">
                  Debug info (tap to expand)
                </summary>
                <p className="mt-1 text-[10px] text-gray-400 font-mono break-all bg-gray-50 rounded p-2 max-h-48 overflow-auto" data-id="element-3514" data-mp="1r2oe8t0yeqkf">
                  {diagnostics}
                </p>
              </details>}
            <p className="mt-2 text-[10px] text-gray-300 font-mono" data-id="element-3515" data-mp="211dyutk8ip4d">
              {BUILD_ID}
            </p>
          </div>}

        <p className="text-center text-xs text-gray-400 mt-6" data-id="element-3516" data-mp="25xcr4s8oora2">{t.poweredBy}</p>
        <p className="text-center text-[9px] text-gray-300 mt-1" data-id="element-3517" data-mp="6nw68s4z322h">{BUILD_ID}</p>
      </div>
    </div>;
}